// frontend/src/pages/Projects.jsx
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

// Role-based authorization helpers
const isSalesperson = (user) =>
  user?.role === 'sales' ||
  /^(sales|bd|business\s*development)$/i.test(user?.department);
const isDesigner = (user) =>
  user?.role === 'employee' && /design/i.test(user?.department);
const isAdminOrManager = (user) =>
  user?.role === 'admin' ||
  (user?.role === 'manager' && !isSalesperson(user) && !isDesigner(user));

// ── Constants ─────────────────────────────────────────────────────────────────
const BHK_TYPES       = ["1 BHK","2 BHK","3 BHK","4 BHK","Villa","Office Space","Other"];
const POSSESSION_OPTS = ["Immediately","In a Month","2-3 Month","3-6 Month","0.6-1 Year"];
const STAGE_OPTS = [
  "Lead Received","Call NR","Not Interested","Irrelevant Lead",
  "In Progress","Quotation Sent","Site Visit Done",
  "Office Visit Done","Revised Quotation Sent","Client Loss","Client Won"
];
const STAGE_TAB_MAP = {
  all:       null,
  sales:     ["Lead Received","Call NR","Not Interested","Irrelevant Lead","In Progress","Quotation Sent","Site Visit Done","Office Visit Done","Revised Quotation Sent"],
  design:    ["Client Won"],
  execution: [],
  handover:  [],
  lost:      ["Client Loss"],
};

const STAGE_COLORS = {
  "Lead Received":          { bg:'#EDE9FE', color:'#6D28D9' },
  "Call NR":                { bg:'#FEF3C7', color:'#B45309' },
  "Not Interested":         { bg:'#FEE2E2', color:'#991B1B' },
  "Irrelevant Lead":        { bg:'#F3F4F6', color:'#374151' },
  "In Progress":            { bg:'#DBEAFE', color:'#976793' },
  "Quotation Sent":         { bg:'#D1FAE5', color:'#065F46' },
  "Site Visit Done":        { bg:'#FFEDD5', color:'#C2410C' },
  "Office Visit Done":      { bg:'#FEF3C7', color:'#92400E' },
  "Revised Quotation Sent": { bg:'#E0E7FF', color:'#3730A3' },
  "Client Loss":            { bg:'#FEE2E2', color:'#991B1B' },
  "Client Won":             { bg:'#D1FAE5', color:'#065F46' },
};

const projectRaw = (project) => project.raw || project.leadSourceData?.crmLeads || project.leadSourceData || {};
const textValue = (value) => String(value || '').toLowerCase().trim();
const hasAnyFilter = (search, filters = {}) =>
  Boolean(textValue(search)) || Object.values(filters || {}).some((value) => Boolean(textValue(value)));

const displayProject = (project) => {
  const raw = projectRaw(project);
  const assignedName =
    project.assignedTo?.name ||
    (typeof project.assignedTo === 'object' ? project.assignedTo?.name : '') ||
    project.client?.assignedTo?.name ||
    (typeof project.client?.assignedTo === 'object' ? project.client?.assignedTo?.name : '') ||
    '';
  const assignedId =
    project.assignedTo?._id ||
    (typeof project.assignedTo === 'string' ? project.assignedTo : '') ||
    project.client?.assignedTo?._id ||
    (typeof project.client?.assignedTo === 'string' ? project.client?.assignedTo : '') ||
    '';

  return {
    createdTime: raw.created_time || project.createdAt?.slice(0, 10) || '',
    adName: raw.ad_name || 'Meta Lead',
    platform: raw.platform || 'Meta',
    fullName: project.client?.name || raw.full_name || project.clientName || '',
    phone: raw.phone_number || project.client?.phone || project.phone || '',
    email: raw.email || project.client?.email || project.email || '',
    address: raw.street_address || project.propertyAddress || project.client?.propertyAddress || '',
    bhk: raw.select_your_bh || project.bhkType || '',
    budget: project.estBudget || raw["what_is_your_budget?"] || '',
    stage: project.stage || raw.lead_status || '',
    assignedTo: assignedId,
    assignedToName: String(assignedName || '').trim(),
    isAssigned: Boolean(assignedId || String(assignedName || '').trim()),
  };
};

const initialsFor = (name = '') =>
  String(name || '')
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'TS';

const matchesProjectFilters = (project, search, filters = {}) => {
  const row = displayProject(project);
  const global = textValue(search);
  if (global) {
    const haystack = [row.fullName, row.phone, row.email, row.address, row.bhk, row.budget, row.stage, row.assignedToName, row.adName, row.platform]
      .map(textValue)
      .join(' ');
    if (!haystack.includes(global)) return false;
  }

  // Client name filter
  if (filters['client.name']) {
    const cf = textValue(filters['client.name']);
    if (cf && !textValue(row.fullName).includes(cf)) return false;
  }

  // Assigned / unassigned / salesperson filter
  if (filters.assignedTo) {
    const af = textValue(filters.assignedTo);
    if (af === 'assigned') {
      if (!row.isAssigned) return false;
    } else if (af === 'unassigned') {
      if (row.isAssigned) return false;
    } else {
      if (textValue(row.assignedTo) !== af && textValue(row.assignedToName) !== af) return false;
    }
  }

  const checks = [
    ['phone', row.phone, 'includes'],
    ['email', row.email, 'includes'],
    ['propertyAddress', row.address, 'includes'],
    ['bhkType', row.bhk, 'exact'],
    ['stage', row.stage, 'exact'],
  ];

  return checks.every(([key, value, mode]) => {
    const filter = textValue(filters[key]);
    if (!filter) return true;
    const candidate = textValue(value);
    return mode === 'exact' ? candidate === filter : candidate.includes(filter);
  });
};

function StageBadge({ stage }) {
  const s = STAGE_COLORS[stage] || { bg:'#F3F4F6', color:'#374151' };
  return (
    <span style={{ display:'inline-flex', alignItems:'center', padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, background:s.bg, color:s.color, whiteSpace:'nowrap' }}>
      {stage || '—'}
    </span>
  );
}

// ── Inline Stage Dropdown (Click & Drop Down Quick Edit) ─────────────────────
function StageDropdown({ projectId, currentStage, onUpdated }) {
  const [stage, setStage] = useState(currentStage || 'Lead Received');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStage(currentStage || 'Lead Received');
  }, [currentStage]);

  const handleChange = async (e) => {
    e.stopPropagation();
    const newStage = e.target.value;
    if (!newStage || newStage === stage) return;

    const prevStage = stage;
    setStage(newStage);
    setSaving(true);
    try {
      await api.patch(`/projects/${projectId}/stage`, { stage: newStage });
      if (onUpdated) onUpdated(projectId, newStage);
    } catch (err) {
      console.error('[StageDropdown] error updating stage:', err);
      setStage(prevStage);
      alert(err.response?.data?.message || 'Failed to update lead status');
    } finally {
      setSaving(false);
    }
  };

  const s = STAGE_COLORS[stage] || { bg: '#F3F4F6', color: '#374151' };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ display: 'inline-flex', alignItems: 'center', position: 'relative', maxWidth: '100%', verticalAlign: 'middle' }}
    >
      <select
        value={stage}
        disabled={saving}
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()}
        title="Click to select lead status"
        aria-label="Lead status"
        style={{
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          background: s.bg,
          color: s.color,
          border: `1px solid ${s.color}40`,
          borderRadius: 16,
          padding: '4px 22px 4px 10px',
          fontSize: 11,
          fontWeight: 600,
          cursor: saving ? 'wait' : 'pointer',
          outline: 'none',
          fontFamily: 'inherit',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          transition: 'all 0.15s ease',
          opacity: saving ? 0.65 : 1,
        }}
      >
        {STAGE_OPTS.map((opt) => (
          <option
            key={opt}
            value={opt}
            style={{
              background: '#fff',
              color: '#1F2937',
              fontSize: 12,
              fontWeight: 500,
              padding: '6px 10px',
            }}
          >
            {opt}
          </option>
        ))}
      </select>
      <span
        style={{
          position: 'absolute',
          right: 7,
          pointerEvents: 'none',
          fontSize: 8,
          color: s.color,
          fontWeight: 800,
          lineHeight: 1,
        }}
      >
        ▼
      </span>
      {saving && (
        <span
          style={{
            position: 'absolute',
            left: -14,
            width: 10,
            height: 10,
            border: '2px solid #E5E7EB',
            borderTopColor: s.color,
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite',
          }}
        />
      )}
    </div>
  );
}

// ── Inline Assign Dropdown (Click & Drop Down Quick Edit) ────────────────────
function AssignDropdown({ projectId, currentAssignedId, currentAssignedName, salespersons = [], onUpdated }) {
  const [assignedId, setAssignedId] = useState(currentAssignedId || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAssignedId(currentAssignedId || '');
  }, [currentAssignedId]);

  const handleChange = async (e) => {
    e.stopPropagation();
    const newId = e.target.value;
    if (newId === assignedId) return;

    const prevId = assignedId;
    setAssignedId(newId);
    setSaving(true);
    try {
      await api.patch(`/projects/${projectId}/assign`, { assignedTo: newId || null });
      if (onUpdated) onUpdated(projectId, newId);
    } catch (err) {
      console.error('[AssignDropdown] error updating assignee:', err);
      setAssignedId(prevId);
      alert(err.response?.data?.message || 'Failed to update assigned salesperson');
    } finally {
      setSaving(false);
    }
  };

  const isAssigned = Boolean(assignedId);

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ display: 'inline-flex', alignItems: 'center', position: 'relative', maxWidth: '100%', verticalAlign: 'middle' }}
    >
      <select
        value={assignedId}
        disabled={saving}
        onChange={handleChange}
        onClick={(e) => e.stopPropagation()}
        title="Click to assign lead / salesperson"
        aria-label="Assign to salesperson"
        style={{
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          background: isAssigned ? '#F7F1F7' : '#F9FAFB',
          color: isAssigned ? '#976793' : '#6B7280',
          border: isAssigned ? '1px solid #E0D0DF' : '1px dashed #D1D5DB',
          borderRadius: 16,
          padding: '4px 22px 4px 10px',
          fontSize: 11,
          fontWeight: 600,
          cursor: saving ? 'wait' : 'pointer',
          outline: 'none',
          fontFamily: 'inherit',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          transition: 'all 0.15s ease',
          opacity: saving ? 0.65 : 1,
        }}
      >
        <option value="" style={{ background: '#fff', color: '#9CA3AF', fontWeight: 500 }}>
          ⚠️ Unassigned
        </option>
        <optgroup label="Sales Person / BD:" style={{ background: '#fff', color: '#374151', fontWeight: 600 }}>
          {salespersons.map((s) => (
            <option
              key={s._id}
              value={s._id}
              style={{
                background: '#fff',
                color: '#1F2937',
                fontSize: 12,
                fontWeight: 500,
                padding: '6px 10px',
              }}
            >
              {s.name?.trim()} {s.department ? `(${s.department})` : ''}
            </option>
          ))}
        </optgroup>
      </select>
      <span
        style={{
          position: 'absolute',
          right: 7,
          pointerEvents: 'none',
          fontSize: 8,
          color: isAssigned ? '#976793' : '#9CA3AF',
          fontWeight: 800,
          lineHeight: 1,
        }}
      >
        ▼
      </span>
      {saving && (
        <span
          style={{
            position: 'absolute',
            left: -14,
            width: 10,
            height: 10,
            border: '2px solid #E5E7EB',
            borderTopColor: '#976793',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite',
          }}
        />
      )}
    </div>
  );
}

// ── Add Project / Lead Modal ──────────────────────────────────────────────────
function AddProjectModal({ salespersons = [], onClose, onSaved }) {
  const [form, setForm] = useState({
    clientName:'', phone:'', email:'', propertyAddress:'',
    pincode:'', location:'',
    estBudget:'', bhkType:'', possessionMonth:'', stage:'Lead Received',
    city:'', state:'',
    assignedTo:'',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const h = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async e => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      await api.post('/projects', {
        ...form,
        estBudget: parseFloat(form.estBudget) || 0,
        assignedTo: form.assignedTo || null,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save. Please try again.');
    } finally { setSaving(false); }
  };

  const iS = { width:'100%', padding:'9px 12px', border:'1px solid #E5E7EB', borderRadius:8, background:'#F9FAFB', fontSize:13, outline:'none', fontFamily:'inherit' };
  const lS = { display:'block', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', color:'#6B7280', marginBottom:5 };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:580, boxShadow:'0 20px 60px rgba(0,0,0,0.15)', maxHeight:'90vh', overflow:'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 24px', borderBottom:'1px solid #E5E7EB', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
          <div>
            <h2 style={{ margin:0, fontSize:17, fontWeight:700, color:'#1F2937' }}>Add New Lead</h2>
            <p style={{ margin:0, fontSize:12, color:'#9CA3AF', marginTop:2 }}>Fill in client details to create a new project</p>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:22, color:'#9CA3AF', cursor:'pointer', lineHeight:1 }}>×</button>
        </div>

        <form onSubmit={submit} style={{ padding:'20px 24px' }}>
          {error && (
            <div style={{ background:'#FEE2E2', border:'1px solid #FECACA', color:'#EF4444', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:16 }}>
              {error}
            </div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
            <div>
              <label style={lS}>Name *</label>
              <input style={iS} name="clientName" value={form.clientName} onChange={h} required placeholder="Client full name" />
            </div>
            <div>
              <label style={lS}>Phone *</label>
              <input style={iS} name="phone" value={form.phone} onChange={h} required placeholder="+91 XXXXX XXXXX" />
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
            <div>
              <label style={lS}>Email</label>
              <input style={iS} name="email" type="email" value={form.email} onChange={h} placeholder="client@email.com" />
            </div>
            <div>
              <label style={lS}>Est. Budget (₹) *</label>
              <input style={iS} name="estBudget" type="number" value={form.estBudget} onChange={h} required min={1} step={1} placeholder="e.g. 700000" />
            </div>
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={lS}>Assign To (Sales / BD)</label>
            <select style={iS} name="assignedTo" value={form.assignedTo} onChange={h}>
              <option value="">Select Sales Person / BD (Optional)</option>
              {salespersons.map(s => (
                <option key={s._id} value={s._id}>
                  {s.name.trim()} {s.department ? `(${s.department})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={lS}>Property Address *</label>
            <input style={iS} name="propertyAddress" value={form.propertyAddress} onChange={h} required placeholder="Full property address" />
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
            <div>
              <label style={lS}>City *</label>
              <input style={iS} name="city" value={form.city} onChange={h} required placeholder="City" />
            </div>
            <div>
              <label style={lS}>State *</label>
              <input style={iS} name="state" value={form.state} onChange={h} required placeholder="State" />
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
            <div>
              <label style={lS}>Pincode</label>
              <input style={iS} name="pincode" value={form.pincode} onChange={h} placeholder="Pincode" />
            </div>
            <div>
              <label style={lS}>Landmark</label>
              <input style={iS} name="location" value={form.location} onChange={h} placeholder="Nearby landmark" />
            </div>
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={lS}>BHK Type *</label>
            <select style={iS} name="bhkType" value={form.bhkType} onChange={h} required>
              <option value="">Select BHK Type</option>
              {BHK_TYPES.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
            <div>
              <label style={lS}>Stage *</label>
              <select style={iS} name="stage" value={form.stage} onChange={h} required>
                {STAGE_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={lS}>Possession Month *</label>
              <select style={iS} name="possessionMonth" value={form.possessionMonth} onChange={h} required>
                <option value="">Select Possession</option>
                {POSSESSION_OPTS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', borderTop:'1px solid #F3F4F6', paddingTop:16 }}>
            <button type="button" onClick={onClose}
              style={{ padding:'9px 20px', border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', fontSize:13, fontWeight:500, cursor:'pointer', color:'#374151' }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              style={{ padding:'9px 20px', background:'#976793', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:saving?'not-allowed':'pointer', opacity:saving?0.7:1 }}>
              {saving ? 'Saving…' : 'Add Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Quick Edit Modal ──────────────────────────────────────────────────────────
function QuickEditModal({ project, salespersons = [], onClose, onSaved }) {
  const display = displayProject(project);
  const [form, setForm] = useState({
    clientName:      display.fullName       || '',
    phone:           display.phone          || '',
    email:           display.email          || '',
    propertyAddress: display.address        || '',
    pincode:         project.client?.address?.pincode || '',
    location:        project.client?.address?.location || '',
    estBudget:       display.budget         || '',
    bhkType:         display.bhk            || '',
    possessionMonth: project.possessionMonth|| '',
    stage:           display.stage          || 'Lead Received',
    city:            project.city           || '',
    state:           project.state          || '',
    assignedTo:      display.assignedTo     || '',
  });
  const [saving, setSaving] = useState(false);

  const h = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await api.put(`/projects/${project._id}`, {
        ...form,
        estBudget: parseFloat(form.estBudget)||0,
        assignedTo: form.assignedTo || null,
      });
      onSaved();
      onClose();
    } catch (err) { alert(err.response?.data?.message || 'Update failed'); }
    finally { setSaving(false); }
  };

  const iS = { width:'100%', padding:'8px 12px', border:'1px solid #E5E7EB', borderRadius:8, background:'#F9FAFB', fontSize:13, outline:'none', fontFamily:'inherit' };
  const lS = { display:'block', fontSize:11, fontWeight:600, color:'#6B7280', marginBottom:4 };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:14, width:'100%', maxWidth:540, boxShadow:'0 20px 60px rgba(0,0,0,0.15)', maxHeight:'85vh', overflow:'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderBottom:'1px solid #E5E7EB', position:'sticky', top:0, background:'#fff' }}>
          <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:'#1F2937' }}>Quick Edit</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, color:'#9CA3AF', cursor:'pointer' }}>×</button>
        </div>
        <form onSubmit={submit} style={{ padding:'16px 20px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div><label style={lS}>Name *</label><input style={iS} name="clientName" value={form.clientName} onChange={h} required /></div>
            <div><label style={lS}>Phone *</label><input style={iS} name="phone" value={form.phone} onChange={h} required /></div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div><label style={lS}>Email</label><input style={iS} name="email" type="email" value={form.email} onChange={h} /></div>
            <div><label style={lS}>Est. Budget *</label><input style={iS} name="estBudget" type="number" value={form.estBudget} onChange={h} required min={1} step={1} /></div>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={lS}>Assign To (Sales / BD)</label>
            <select style={iS} name="assignedTo" value={form.assignedTo} onChange={h}>
              <option value="">Unassigned</option>
              {salespersons.map(s => (
                <option key={s._id} value={s._id}>
                  {s.name.trim()} {s.department ? `(${s.department})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom:12 }}><label style={lS}>Property Address *</label><input style={iS} name="propertyAddress" value={form.propertyAddress} onChange={h} required /></div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div><label style={lS}>City *</label><input style={iS} name="city" value={form.city} onChange={h} required /></div>
            <div><label style={lS}>State *</label><input style={iS} name="state" value={form.state} onChange={h} required /></div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div><label style={lS}>Pincode</label><input style={iS} name="pincode" value={form.pincode} onChange={h} /></div>
            <div><label style={lS}>Landmark</label><input style={iS} name="location" value={form.location} onChange={h} placeholder="Nearby landmark" /></div>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={lS}>BHK Type *</label>
            <select style={iS} name="bhkType" value={form.bhkType} onChange={h} required>
              <option value="">Select</option>
              {BHK_TYPES.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div>
              <label style={lS}>Stage *</label>
              <select style={iS} name="stage" value={form.stage} onChange={h} required>
                {STAGE_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={lS}>Possession Month *</label>
              <select style={iS} name="possessionMonth" value={form.possessionMonth} onChange={h} required>
                <option value="">Select</option>
                {POSSESSION_OPTS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', borderTop:'1px solid #F3F4F6', paddingTop:14 }}>
            <button type="button" onClick={onClose} style={{ padding:'8px 18px', border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', fontSize:13, cursor:'pointer' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding:'8px 18px', background:'#976793', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
              {saving ? 'Saving…' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditableCell({ value, onSave, type = "text" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");

  useEffect(() => setVal(value || ""), [value]);

  const save = async () => {
    setEditing(false);
    if (val === value) return;
    await onSave(val);
  };

  if (editing) {
    return (
      <input
        autoFocus
        type={type}
        value={val}
        onClick={(e) => e.stopPropagation()}
        onChange={e => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === "Enter") save(); }}
        style={{ width:'100%', padding:'4px 6px', border:'1px solid #976793', borderRadius:6, fontSize:13 }}
      />
    );
  }

  return (
    <div
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      style={{ cursor:'pointer', minHeight:20 }}
    >
      {value || "—"}
    </div>
  );
}

// ── Main Projects Page ────────────────────────────────────────────────────────
export default function Projects() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user }  = useAuth();
  const isAdmin   = isAdminOrManager(user);
  const isSalespersonRole = isSalesperson(user);
  const isDesignerRole = isDesigner(user);

  const [projects, setProjects]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [tabCounts, setTabCounts] = useState({});
  const [loading, setLoading]     = useState(true);  // true only on very first load
  const [fetching, setFetching]   = useState(false); // true on every subsequent fetch (subtle indicator, table stays mounted)

  const [activeTab, setActiveTab] = useState('all');
  const [page, setPage]           = useState(1);
  const [limit, setLimit]         = useState(25);
  const [search, setSearch]       = useState('');
  const [salespersons, setSalespersons] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/users', { params: { isActive: 'true' } });
        setSalespersons(res.data.data?.users || []);
      } catch (err) {
        console.error('Failed to load team members:', err);
      }
    })();
  }, []);

  // ── Column filters: stored locally, debounced before triggering API ──────
  // `colFilters`    = what the user is currently typing (live, bound to inputs)
  // `appliedFilters` = debounced copy that actually fires the API call
  const [colFilters, setColFilters]     = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const debounceRef = useRef(null);
  const stageFromUrl = searchParams.get('stage') || '';

  // Debounce: 400 ms after the last keystroke, copy colFilters → appliedFilters
  const handleColFilterChange = useCallback((key, value) => {
    // Update the input value immediately (no re-fetch yet)
    setColFilters(prev => ({ ...prev, [key]: value }));

    // Reset page to 1 immediately
    setPage(1);

    // Debounce the actual API trigger
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setAppliedFilters(prev => ({ ...prev, [key]: value }));
    }, 400);
  }, []);

  useEffect(() => {
    if (stageFromUrl && STAGE_OPTS.includes(stageFromUrl)) {
      setActiveTab('all');
      setPage(1);
      setColFilters(prev => ({ ...prev, stage: stageFromUrl }));
      setAppliedFilters(prev => ({ ...prev, stage: stageFromUrl }));
      return;
    }
    setColFilters(prev => prev.stage ? ({ ...prev, stage: '' }) : prev);
    setAppliedFilters(prev => prev.stage ? ({ ...prev, stage: '' }) : prev);
  }, [stageFromUrl]);

  // Clean up debounce on unmount
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const [showModal, setShowModal]       = useState(false);
  const [showExcel, setShowExcel]       = useState(false);
  const [editProject, setEditProject]   = useState(null);

  const [selectedIds, setSelectedIds]   = useState(new Set());
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef                     = useRef();

  const fileInputRef = useRef();
  const excelRef     = useRef();

  // ── API load — depends on appliedFilters (debounced), NOT colFilters ──────
  // isInitial flag: only the very first fetch (empty table) shows the full-page spinner.
  // All subsequent fetches show only a thin progress bar so the table stays mounted
  // and filter inputs NEVER lose focus.
  const isInitialLoad = useRef(true);

  const load = useCallback(async () => {
    if (isInitialLoad.current) {
      setLoading(true);
    } else {
      setFetching(true);
    }
    try {
      const filtering = hasAnyFilter(search, appliedFilters);
      const requestLimit = filtering ? 10000 : limit;
      const params = { page: filtering ? 1 : page, limit: requestLimit, tab: activeTab };

      // Global search bar
      if (search.trim()) params.search = search.trim();

      // Column filters (debounced copy)
      if (appliedFilters.propertyAddress) params.propertyAddress = appliedFilters.propertyAddress;
      if (appliedFilters['client.name'])  params.fullName        = appliedFilters['client.name'];
      if (appliedFilters.phone)           params.phone           = appliedFilters.phone;
      if (appliedFilters.email)           params.email           = appliedFilters.email;
      if (appliedFilters.stage)           params.stage           = appliedFilters.stage;
      if (appliedFilters.bhkType)         params.bhkType         = appliedFilters.bhkType;
      if (appliedFilters.possessionMonth) params.possessionMonth = appliedFilters.possessionMonth;
      if (appliedFilters.city)            params.city            = appliedFilters.city;
      if (appliedFilters.state)           params.state           = appliedFilters.state;
      if (appliedFilters.assignedTo)      params.assignedTo      = appliedFilters.assignedTo;

      const r = await api.get('/projects', { params });
      const loadedProjects = r.data.data.projects || [];
      const clientFilteredProjects = filtering
        ? loadedProjects.filter((project) => matchesProjectFilters(project, search, appliedFilters))
        : loadedProjects;
      setProjects(clientFilteredProjects);
      setTotal(filtering ? clientFilteredProjects.length : (r.data.data.pagination?.total || 0));
      setTabCounts(r.data.data.tabCounts || {});
    } catch (e) { console.error(e); }
    finally {
      setLoading(false);
      setFetching(false);
      isInitialLoad.current = false;
    }
  }, [page, limit, activeTab, search, appliedFilters]); // ← appliedFilters, not colFilters

  useEffect(() => { load(); }, [load]);

  const saveField = async (id, field, value) => {
    try {
      const row = projects.find(p => p._id === id);
      if (!row) return;
      const payload = {
        clientName: row.clientName || '',
        phone: row.phone || '',
        email: row.email || '',
        propertyAddress: row.propertyAddress || '',
        estBudget: Number(row.estBudget || 0),
        bhkType: row.bhkType || '',
        stage: row.stage || '',
        city: row.city || '',
        state: row.state || '',
        possessionMonth: row.possessionMonth || '',
        [field]: field === 'estBudget' ? Number(value || 0) : value
      };
      await api.put(`/projects/${id}`, payload);
      load();
    } catch (e) { console.error(e); alert("Save failed"); }
  };

  // Close excel dropdown on outside click
  useEffect(() => {
    const close = () => setShowExcel(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target))
        setShowSettings(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const handleStageUpdated = (id, newStage) => {
    setProjects(ps => ps.map(p => p._id === id ? {
      ...p,
      stage: newStage,
      raw: p.raw ? { ...p.raw, lead_status: newStage } : p.raw,
      leadSourceData: p.leadSourceData ? { ...p.leadSourceData, lead_status: newStage } : p.leadSourceData,
    } : p));
    load();
  };

  const handleAssignedUpdated = (id, newAssigneeId) => {
    const matched = salespersons.find(s => s._id === newAssigneeId);
    setProjects(ps => ps.map(p => {
      if (p._id !== id) return p;
      return {
        ...p,
        assignedTo: matched ? { _id: matched._id, name: matched.name, department: matched.department, role: matched.role } : null,
        client: p.client && typeof p.client === 'object'
          ? { ...p.client, assignedTo: matched ? { _id: matched._id, name: matched.name } : null }
          : p.client,
      };
    }));
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try { await api.delete(`/projects/${id}`); load(); }
    catch (e) { alert('Delete failed'); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected project(s)?`)) return;
    try {
      await Promise.all([...selectedIds].map(id => api.delete(`/projects/${id}`)));
      setSelectedIds(new Set());
      load();
    } catch (e) { alert('Bulk delete failed'); }
  };

  const toggleSelectAll = (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (shownProjects.length === 0) return;
    if (selectedIds.size === shownProjects.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(shownProjects.map(p => p._id)));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // ── Excel Export ──────────────────────────────────────────────────────────
  const handleExport = async () => {
    try {
      const r = await api.get('/projects', { params: { tab: activeTab, limit: 10000 } });
      const data = r.data.data.projects || [];
      const rows = [
        ['Client Name', 'Phone', 'Email', 'Property Address', 'Est. Budget', 'BHK Type', 'Possession Month', 'Stage', 'Assigned To', 'City', 'State', 'Created On'],
        ...data.map(p => [
          p.clientName || '',
          p.phone || '',
          p.email || '',
          p.propertyAddress || '',
          p.estBudget || 0,
          p.bhkType || '',
          p.possessionMonth || '',
          p.stage || '',
          p.assignedTo?.name || p.client?.assignedTo?.name || '',
          p.city || '',
          p.state || '',
          p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-IN') : '',
        ])
      ];
      const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `projects-${activeTab}-${Date.now()}.csv`;
      a.click();
    } catch (e) { alert('Export failed'); }
    setShowExcel(false);
  };

  // ── Excel Import ──────────────────────────────────────────────────────────
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post('/projects/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert('Import successful!');
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Import failed');
    }
    e.target.value = '';
    setShowExcel(false);
  };

  const isFiltering = hasAnyFilter(search, colFilters);
  const filteredProjects = useMemo(
    () => projects.filter((project) => matchesProjectFilters(project, search, colFilters)),
    [projects, search, colFilters]
  );
  const shownProjects = isFiltering
    ? filteredProjects.slice((page - 1) * limit, page * limit)
    : projects;
  const displayTotal = isFiltering ? filteredProjects.length : total;
  const totalPages = Math.ceil(displayTotal / limit) || 1;

  const openClientWhatsApp = (project) => {
    const row = displayProject(project);
    const digits = String(row.phone || '').replace(/\D/g, '');
    const phoneNumber = digits.length > 10 ? digits.slice(-10) : digits;
    if (!phoneNumber) {
      alert('Phone number is missing for this client.');
      return;
    }
    window.open(`https://wa.me/91${phoneNumber}`, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const TABS = [
    { key: 'all',       label: 'All',       count: total },
    { key: 'sales',     label: 'Sales',     count: tabCounts.sales     || 0 },
    { key: 'design',    label: 'Design',    count: tabCounts.design    || 0 },
    { key: 'execution', label: 'Execution', count: tabCounts.execution || 0 },
    { key: 'handover',  label: 'Handover',  count: tabCounts.handover  || 0 },
    { key: 'lost',      label: 'Lost',      count: tabCounts.lost      || 0 },
  ];

  // Role-based header text
  const getHeaderText = () => {
    if (isSalespersonRole) return 'My Projects';
    if (isDesignerRole) return 'Design Projects';
    return 'All Projects';
  };

  const getHeaderSubtitle = () => {
    if (isSalespersonRole) return 'Projects assigned to you';
    if (isDesignerRole) return 'Projects that have reached "Client Won" stage';
    return 'Manage all projects and leads';
  };

  // Set default tab based on role
  useEffect(() => {
    if (isDesignerRole && activeTab === 'all') {
      setActiveTab('design');
    } else if (isSalespersonRole && activeTab === 'all') {
      setActiveTab('sales');
    }
  }, [isDesignerRole, isSalespersonRole, activeTab]);

  const thS = {
    textAlign:'left', padding:'10px 8px', fontSize:11, fontWeight:700,
    color:'#6B7280', borderBottom:'2px solid #E5E7EB', whiteSpace:'nowrap',
    background:'#fff', position:'sticky', top:0, zIndex:1,
    textTransform:'uppercase', letterSpacing:'0.05em', boxSizing:'border-box',
  };
  const tdS = { padding:'10px 8px', fontSize:13, color:'#374151', borderBottom:'1px solid #F3F4F6', verticalAlign:'middle', boxSizing:'border-box' };
  const tableColumns = ['Created Time','Full Name','Phone','Email','Address','BHK','Budget','Assigned To','Lead Status','Actions'];
  const columnWidths = {
    'Created Time': 112,
    'Full Name': 120,
    Phone: 110,
    Email: 184,
    Address: 160,
    BHK: 66,
    Budget: 78,
    'Assigned To': 160,
    'Lead Status': 160,
    Actions: 86,
  };
  const widthForColumn = (col) => columnWidths[col] ? { width: columnWidths[col], minWidth: columnWidths[col] } : {};
  const filterCellStyle = (col, i) => ({
    padding:'4px 8px',
    borderBottom:'1px solid #E5E7EB',
    ...(col ? widthForColumn(tableColumns[i]) : {}),
  });
  const filterSelectStyle = { width:'100%', minWidth:0, padding:'3px 22px 3px 6px', border:'1px solid #E5E7EB', borderRadius:4, fontSize:11, boxSizing:'border-box' };
  const wrapCellStyle = { ...tdS, overflowWrap:'anywhere', wordBreak:'break-word' };

  return (
    <div className="projects-page-shell" style={{ display:'flex', flexDirection:'column', minHeight:'calc(100vh - var(--nav-h) - 4rem)', width:'100%', minWidth:0, fontFamily:'inherit', background:'#F8F9FB' }}>

      {/* ── Top Bar ── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #E5E7EB', flexShrink:0 }}>
        <div className="projects-mobile-topbar" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, padding:'14px 20px', width:'100%', minWidth:0 }}>
          <div>
            <h1 style={{ margin:0, fontSize:20, fontWeight:700, color:'#1F2937' }}>{getHeaderText()}</h1>
            <p style={{ margin:0, fontSize:12, color:'#9CA3AF', marginTop:2 }}>{getHeaderSubtitle()}</p>
          </div>

          <div className="projects-mobile-toolbar" style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', flexWrap:'wrap', gap:10, minWidth:0 }}>
            {/* Search */}
            <div className="projects-mobile-search" style={{ position:'relative' }}>
              <svg style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#9CA3AF' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <input
                style={{ paddingLeft:32, paddingRight:12, paddingTop:7, paddingBottom:7, border:'1px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', width:'clamp(220px, 22vw, 360px)', maxWidth:'100%', fontFamily:'inherit' }}
                placeholder="Search projects…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </div>

            {/* Client Filter */}
            <div className="projects-mobile-action" style={{ position:'relative' }}>
              <input
                aria-label="Filter by Client"
                style={{
                  padding: '7px 24px 7px 12px',
                  border: '1px solid #E5E7EB',
                  borderRadius: 8,
                  fontSize: 13,
                  outline: 'none',
                  fontFamily: 'inherit',
                  background: colFilters['client.name'] ? '#F7F1F7' : '#fff',
                  color: colFilters['client.name'] ? '#976793' : '#374151',
                  fontWeight: colFilters['client.name'] ? 600 : 400,
                  borderColor: colFilters['client.name'] ? '#C7D2FE' : '#E5E7EB',
                  width: 'clamp(140px, 14vw, 190px)',
                  maxWidth: '100%',
                }}
                placeholder="Filter Client…"
                value={colFilters['client.name'] || ''}
                onChange={e => handleColFilterChange('client.name', e.target.value)}
              />
              {colFilters['client.name'] && (
                <button
                  type="button"
                  onClick={() => handleColFilterChange('client.name', '')}
                  style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', fontSize:14, color:'#9CA3AF', cursor:'pointer', padding:0, lineHeight:1 }}
                  title="Clear client filter"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Assigned Client / Sales Person Filter */}
            <div className="projects-mobile-action" style={{ position:'relative' }}>
              <select
                aria-label="Filter by Assigned Client"
                value={colFilters.assignedTo || ''}
                onChange={e => handleColFilterChange('assignedTo', e.target.value)}
                style={{
                  padding:'7px 12px',
                  border:'1px solid #E5E7EB',
                  borderRadius:8,
                  fontSize:13,
                  background: colFilters.assignedTo ? '#F7F1F7' : '#fff',
                  color: colFilters.assignedTo ? '#976793' : '#374151',
                  fontWeight: colFilters.assignedTo ? 600 : 400,
                  borderColor: colFilters.assignedTo ? '#C7D2FE' : '#E5E7EB',
                  outline:'none',
                  cursor:'pointer',
                  fontFamily:'inherit',
                  maxWidth: 220,
                }}
              >
                <option value="">All Clients (All Leads)</option>
                <option value="assigned">✓ Assigned Clients Only</option>
                <option value="unassigned">⚠️ Unassigned Clients Only</option>
                <optgroup label="Assigned Sales Person / BD:">
                  {salespersons.map(s => (
                    <option key={s._id} value={s._id}>
                      {s.name?.trim()} {s.department ? `(${s.department})` : ''}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>

            {/* Excel Button */}
            <div className="projects-mobile-action" style={{ position:'relative' }}>
              <button
                onClick={e => { e.stopPropagation(); setShowExcel(o => !o); }}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', background:'#F7F1F7', border:'1px solid #C7D2FE', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', color:'#976793' }}
              >
                📊 Excel <span style={{ fontSize:10 }}>▼</span>
              </button>
              {showExcel && (
                <div style={{ position:'absolute', top:'110%', right:0, background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, boxShadow:'0 4px 20px rgba(0,0,0,0.1)', zIndex:200, overflow:'hidden', minWidth:160 }} onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => { fileInputRef.current?.click(); }}
                    style={{ display:'flex', alignItems:'center', gap:8, width:'100%', padding:'10px 14px', border:'none', background:'transparent', fontSize:13, cursor:'pointer', color:'#374151', textAlign:'left' }}
                    onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                  >
                    📥 Import Excel
                  </button>
                  <button
                    onClick={handleExport}
                    style={{ display:'flex', alignItems:'center', gap:8, width:'100%', padding:'10px 14px', border:'none', background:'transparent', fontSize:13, cursor:'pointer', color:'#374151', textAlign:'left' }}
                    onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                  >
                    📤 Export Excel
                  </button>
                </div>
              )}
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display:'none' }} onChange={handleImport} />
            </div>

            {/* New Project */}
            {isAdmin && (
              <button
                className="projects-mobile-action"
                onClick={() => setShowModal(true)}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 16px', background:'#976793', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                New Project
              </button>
            )}

            {/* Settings icon */}
            <div ref={settingsRef} className="projects-mobile-action" style={{ position:'relative' }}>
              <button
                onClick={e => { e.stopPropagation(); setShowSettings(o => !o); }}
                style={{ width:34, height:34, border:'1px solid #E5E7EB', borderRadius:8, background: showSettings ? '#F3F4F6' : '#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#6B7280' }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              </button>
              {showSettings && (
                <div style={{ position:'absolute', top:'110%', right:0, background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, boxShadow:'0 4px 20px rgba(0,0,0,0.1)', zIndex:200, overflow:'hidden', minWidth:200 }}>
                  {selectedIds.size > 0 && (
                    <div style={{ padding:'8px 14px', borderBottom:'1px solid #F3F4F6', fontSize:12, color:'#6B7280', fontWeight:600 }}>
                      {selectedIds.size} row{selectedIds.size > 1 ? 's' : ''} selected
                    </div>
                  )}
                  <button
                    onClick={() => { handleBulkDelete(); setShowSettings(false); }}
                    disabled={selectedIds.size === 0}
                    style={{ display:'flex', alignItems:'center', gap:8, width:'100%', padding:'10px 14px', border:'none', background:'transparent', fontSize:13, cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer', color: selectedIds.size === 0 ? '#D1D5DB' : '#EF4444', textAlign:'left' }}
                    onMouseEnter={e => { if (selectedIds.size > 0) e.currentTarget.style.background='#FEF2F2'; }}
                    onMouseLeave={e => { e.currentTarget.style.background='transparent'; }}
                  >
                    🗑️ Delete Selected ({selectedIds.size})
                  </button>
                  <button
                    onClick={toggleSelectAll}
                    style={{ display:'flex', alignItems:'center', gap:8, width:'100%', padding:'10px 14px', border:'none', background:'transparent', fontSize:13, cursor:'pointer', color:'#374151', textAlign:'left' }}
                    onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                  >
                    Select {selectedIds.size === shownProjects.length && shownProjects.length > 0 ? 'None' : 'All on Page'}
                  </button>
                  {selectedIds.size > 0 && (
                    <button
                      onClick={() => { setSelectedIds(new Set()); setShowSettings(false); }}
                      style={{ display:'flex', alignItems:'center', gap:8, width:'100%', padding:'10px 14px', border:'none', background:'transparent', fontSize:13, cursor:'pointer', color:'#374151', textAlign:'left' }}
                      onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}
                    >
                      ✕ Clear Selection
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stage Tabs */}
        <div className="projects-mobile-tabs" style={{ display:'flex', alignItems:'center', borderTop:'1px solid #F3F4F6', padding:'0 20px' }}>
          {TABS.map(tab => (
            <button key={tab.key}
              onClick={() => { setSearchParams({}); setActiveTab(tab.key); setPage(1); }}
              style={{ padding:'10px 14px', fontSize:13, fontWeight:activeTab===tab.key?700:400, color:activeTab===tab.key?'#976793':'#6B7280', background:'transparent', border:'none', borderBottom:activeTab===tab.key?'2px solid #976793':'2px solid transparent', cursor:'pointer', whiteSpace:'nowrap', marginBottom:-1 }}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
          <button style={{ padding:'10px 10px', fontSize:18, background:'transparent', border:'none', color:'#9CA3AF', cursor:'pointer', marginBottom:-1 }}>+</button>
        </div>
      </div>

      {/* Thin progress bar — shown during any re-fetch so table stays mounted and inputs keep focus */}
      {fetching && (
        <div style={{ height:3, background:'#F7F1F7', flexShrink:0 }}>
          <div style={{ height:'100%', background:'#976793', animation:'progressBar 1.4s ease-in-out infinite', transformOrigin:'left' }} />
        </div>
      )}

      {/* ── Table ── */}
      <div style={{ flex:1, minWidth:0, overflow:'auto' }}>
        {loading ? (
          /* Full spinner only on very first load (empty table) */
          <div style={{ textAlign:'center', padding:'4rem' }}>
            <div style={{ width:36, height:36, border:'3px solid #E5E7EB', borderTopColor:'#976793', borderRadius:'50%', animation:'spin 0.7s linear infinite', margin:'0 auto' }} />
          </div>
        ) : (
          <>
          <div className="tablet-split-view">
            <div className="tablet-list-pane">
              <div className="tablet-pane-title">Projects</div>
              {shownProjects.map((p, index) => {
                const row = displayProject(p);
                return (
                  <div
                    className="mobile-card-row"
                    key={p._id}
                    onClick={() => navigate(`/projects/${p._id}`)}
                    style={{ cursor:'pointer', ...(index === 0 ? { background:'var(--mauve-pale)', borderRadius:8, padding:'0.75rem' } : {}) }}
                  >
                    <div className="mobile-avatar">{initialsFor(row.fullName)}</div>
                    <div className="mobile-row-main">
                      <span className="mobile-row-title">{row.fullName || 'Unnamed project'}</span>
                      <span className="mobile-row-sub">{row.address || row.bhk || 'No address'} · {row.budget || 'No budget'}</span>
                      <div onClick={e => e.stopPropagation()} style={{ marginTop: 4 }}>
                        <AssignDropdown
                          projectId={p._id}
                          currentAssignedId={row.assignedTo}
                          currentAssignedName={row.assignedToName}
                          salespersons={salespersons}
                          onUpdated={handleAssignedUpdated}
                        />
                      </div>
                    </div>
                    <div onClick={e => e.stopPropagation()}>
                      <StageDropdown projectId={p._id} currentStage={row.stage} onUpdated={handleStageUpdated} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="tablet-detail-pane">
              {shownProjects[0] ? (() => {
                const row = displayProject(shownProjects[0]);
                return (
                  <>
                    <div className="tablet-detail-title">{row.fullName || 'Project'}</div>
                    <div className="tablet-detail-sub">{row.phone || 'No phone'} · {row.address || 'No address'}</div>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12, alignItems:'center' }}>
                      <div onClick={e => e.stopPropagation()}>
                        <StageDropdown projectId={shownProjects[0]._id} currentStage={row.stage} onUpdated={handleStageUpdated} />
                      </div>
                      <div onClick={e => e.stopPropagation()}>
                        <AssignDropdown
                          projectId={shownProjects[0]._id}
                          currentAssignedId={row.assignedTo}
                          currentAssignedName={row.assignedToName}
                          salespersons={salespersons}
                          onUpdated={handleAssignedUpdated}
                        />
                      </div>
                      {row.bhk && <span className="badge badge-inactive">{row.bhk}</span>}
                      {row.budget && <span className="badge badge-pending">Budget: {row.budget}</span>}
                    </div>
                    <div className="tablet-empty-panel">Open a project to view files, notes, quotes and payments</div>
                  </>
                );
              })() : (
                <div className="tablet-empty-panel">No projects found</div>
              )}
            </div>
          </div>
          <div className="mobile-card-list" style={{ padding:'0 14px 12px', background:'#fff' }}>
            {shownProjects.length === 0 ? (
              <div className="empty-state" style={{ padding:'2rem 1rem' }}>
                <h3>No Projects Found</h3>
                <p>Try a different filter or add a new project.</p>
              </div>
            ) : shownProjects.map(p => {
              const row = displayProject(p);
              return (
                <div
                  className="mobile-card-row"
                  key={p._id}
                  onClick={() => navigate(`/projects/${p._id}`)}
                  style={{ cursor:'pointer' }}
                >
                  <div className="mobile-avatar">{initialsFor(row.fullName)}</div>
                  <div className="mobile-row-main">
                    <span className="mobile-row-title">{row.fullName || 'Unnamed project'}</span>
                    <span className="mobile-row-sub">{row.phone || 'No phone'} · {row.address || row.bhk || 'No address'}</span>
                    <div onClick={e => e.stopPropagation()} style={{ marginTop: 4 }}>
                      <AssignDropdown
                        projectId={p._id}
                        currentAssignedId={row.assignedTo}
                        currentAssignedName={row.assignedToName}
                        salespersons={salespersons}
                        onUpdated={handleAssignedUpdated}
                      />
                    </div>
                  </div>
                  <div className="mobile-row-end" onClick={e => e.stopPropagation()}>
                    <StageDropdown projectId={p._id} currentStage={row.stage} onUpdated={handleStageUpdated} />
                  </div>
                </div>
              );
            })}
          </div>
          {/* Table stays mounted after first load so filter inputs keep focus. */}
          <table className="desktop-table-mobile-hidden tablet-table-hidden" style={{ width:'100%', borderCollapse:'collapse', minWidth:1040, tableLayout:'fixed', opacity: fetching ? 0.55 : 1, transition:'opacity 0.2s' }}>
            <thead>
              <tr>
                <th
                  style={{ ...thS, width: 40, padding: '10px 8px', cursor: 'pointer' }}
                  onClick={toggleSelectAll}
                >
                  <input
                    type="checkbox"
                    checked={shownProjects.length > 0 && selectedIds.size === shownProjects.length}
                    ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < shownProjects.length; }}
                    onClick={e => e.stopPropagation()}
                    onChange={toggleSelectAll}
                    style={{ cursor: 'pointer', width: 16, height: 16 }}
                    aria-label="Select all projects on page"
                  />
                </th>
                {tableColumns.map(col => (
                  <th key={col} style={{ ...thS, ...widthForColumn(col) }}>{col}</th>
                ))}
              </tr>

              {/* ── Column filter row ── */}
              {/* 
                  KEY FIX: inputs use `colFilters` (live) for their value,
                  but call handleColFilterChange which debounces the API call.
                  This means the input is NEVER re-mounted mid-keystroke,
                  so focus is never lost.
              */}
              <tr style={{ background:'#F9FAFB' }}>
                <td style={{ padding:'4px 8px', borderBottom:'1px solid #E5E7EB' }} />
                {[null, 'client.name', 'phone', 'email', 'propertyAddress', 'bhkType', 'estBudget', 'assignedTo', 'stage', null].map((col, i) => (
                  <td key={i} style={filterCellStyle(col, i)}>
                    {!col ? null :
                      col === 'stage' ? (
                        <select
                          style={filterSelectStyle}
                          value={colFilters.stage || ''}
                          onChange={e => {
                            const stage = e.target.value;
                            setSearchParams(stage ? { stage } : {});
                            handleColFilterChange('stage', stage);
                          }}
                        >
                          <option value="">All stages</option>
                          {STAGE_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : col === 'assignedTo' ? (
                        <select
                          style={filterSelectStyle}
                          value={colFilters.assignedTo || ''}
                          onChange={e => handleColFilterChange('assignedTo', e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="assigned">✓ Assigned</option>
                          <option value="unassigned">⚠️ Unassigned</option>
                          <optgroup label="By Assignee:">
                            {salespersons.map(s => (
                              <option key={s._id} value={s._id}>{s.name?.trim()}</option>
                            ))}
                          </optgroup>
                        </select>
                      ) : col === 'bhkType' ? (
                        <select
                          style={filterSelectStyle}
                          value={colFilters.bhkType || ''}
                          onChange={e => handleColFilterChange('bhkType', e.target.value)}
                        >
                          <option value="">All</option>
                          {BHK_TYPES.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                      ) : col === 'estBudget' ? null : (
                        <input
                          style={{ width:'100%', padding:'3px 6px', border:'1px solid #E5E7EB', borderRadius:4, fontSize:11 }}
                          placeholder={col === 'client.name' ? "Filter client…" : "Filter…"}
                          value={colFilters[col] || ''}
                          onChange={e => handleColFilterChange(col, e.target.value)}
                        />
                      )
                    }
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownProjects.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ textAlign:'center', padding:'4rem', color:'#9CA3AF' }}>
                    <div style={{ fontSize:40, marginBottom:8 }}>📋</div>
                    <div style={{ fontSize:16, fontWeight:600, color:'#374151', marginBottom:4 }}>No Projects Found</div>
                    <div style={{ fontSize:13 }}>Try a different filter or add a new project</div>
                  </td>
                </tr>
              ) : shownProjects.map(p => {
                const row = displayProject(p);
                return (
                <tr
                  key={p._id}
                  onClick={() => navigate(`/projects/${p._id}`)}
                  style={{ borderBottom:'1px solid #F3F4F6', cursor:'pointer', transition:'all .15s', background: selectedIds.has(p._id) ? '#F7F1F7' : undefined }}
                  onMouseEnter={e => { if (!selectedIds.has(p._id)) e.currentTarget.style.background='#F9FAFB'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = selectedIds.has(p._id) ? '#F7F1F7' : '#fff'; }}
                >
                  <td
                    style={{ ...tdS, width: 40, padding: '10px 8px', cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelect(p._id);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(p._id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(p._id)}
                      style={{ cursor: 'pointer', width: 16, height: 16 }}
                      aria-label={`Select ${row.fullName || 'project'}`}
                    />
                  </td>
                  <td style={tdS}>{row.createdTime || '-'}</td>
                  <td style={tdS}>{row.fullName || '-'}</td>
                  <td style={tdS}>{row.phone || '-'}</td>
                  <td style={wrapCellStyle}>{row.email || '-'}</td>
                  <td style={wrapCellStyle}>{row.address || '-'}</td>
                  <td style={tdS}>{row.bhk || '-'}</td>
                  <td style={tdS}>{row.budget || '-'}</td>
                  <td style={tdS}>
                    <AssignDropdown
                      projectId={p._id}
                      currentAssignedId={row.assignedTo}
                      currentAssignedName={row.assignedToName}
                      salespersons={salespersons}
                      onUpdated={handleAssignedUpdated}
                    />
                  </td>
                  <td style={tdS}>
                    <StageDropdown projectId={p._id} currentStage={row.stage} onUpdated={handleStageUpdated} />
                  </td>
                  <td style={{ ...tdS, whiteSpace:'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => openClientWhatsApp(p)}
                      aria-label={`Open WhatsApp chat for ${row.fullName || 'client'}`}
                      title="Open WhatsApp"
                      style={{ width:30, height:30, marginRight:6, border:'1px solid #BBF7D0', borderRadius:7, background:'#F0FDF4', color:'#16A34A', cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center' }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.38a9.87 9.87 0 0 0 4.76 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.51 2 12.04 2Zm0 18.16h-.01a8.2 8.2 0 0 1-4.18-1.14l-.3-.18-3.13.82.84-3.05-.2-.31a8.18 8.18 0 0 1-1.25-4.39c0-4.54 3.69-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.41a8.17 8.17 0 0 1 2.41 5.82c0 4.54-3.7 8.25-8.24 8.25Zm4.51-6.17c-.25-.12-1.46-.72-1.69-.8-.23-.08-.39-.12-.56.12-.17.25-.64.8-.79.96-.14.17-.29.19-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.71-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.49-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.84-.86 2.04s.89 2.37 1.02 2.53c.12.17 1.76 2.69 4.26 3.77.6.26 1.06.41 1.42.53.6.19 1.14.16 1.57.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.08.14-1.18-.06-.1-.23-.16-.48-.28Z"/></svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditProject(p)}
                      aria-label={`Edit ${row.fullName || 'project'}`}
                      title="Edit"
                      style={{ width:30, height:30, border:'1px solid #E5E7EB', borderRadius:7, background:'#fff', color:'#976793', cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </>
        )}
      </div>

      {/* ── Pagination ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 20px', borderTop:'1px solid #E5E7EB', background:'#fff', flexShrink:0, fontSize:13, color:'#6B7280' }}>
        <span>
          Showing {displayTotal === 0 ? 0 : (page-1)*limit+1} to {Math.min(page*limit, displayTotal)} of {displayTotal} items
        </span>
        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
          <button disabled={page===1} onClick={() => setPage(p=>p-1)}
            style={{ width:28, height:28, border:'1px solid #E5E7EB', borderRadius:6, background:'#fff', cursor:page===1?'not-allowed':'pointer', color:page===1?'#D1D5DB':'#374151', display:'flex', alignItems:'center', justifyContent:'center' }}>‹</button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i+1).map(n => (
            <button key={n} onClick={() => setPage(n)}
              style={{ width:28, height:28, border:'1px solid #E5E7EB', borderRadius:6, background:page===n?'#976793':'#fff', color:page===n?'#fff':'#374151', cursor:'pointer', fontSize:12, fontWeight:page===n?700:400 }}>
              {n}
            </button>
          ))}
          {totalPages > 7 && (
            <><span>…</span>
              <button onClick={() => setPage(totalPages)} style={{ width:28, height:28, border:'1px solid #E5E7EB', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12 }}>{totalPages}</button>
            </>
          )}
          <button disabled={page===totalPages} onClick={() => setPage(p=>p+1)}
            style={{ width:28, height:28, border:'1px solid #E5E7EB', borderRadius:6, background:'#fff', cursor:page===totalPages?'not-allowed':'pointer', color:page===totalPages?'#D1D5DB':'#374151', display:'flex', alignItems:'center', justifyContent:'center' }}>›</button>
          <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft:8, padding:'4px 8px', border:'1px solid #E5E7EB', borderRadius:6, fontSize:12, outline:'none', fontFamily:'inherit' }}>
            {[10,25,50,100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {/* Modals */}
      {showModal   && <AddProjectModal onClose={() => setShowModal(false)} onSaved={load} salespersons={salespersons} />}
      {editProject && <QuickEditModal project={editProject} onClose={() => setEditProject(null)} onSaved={load} salespersons={salespersons} />}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }
        @keyframes progressBar { 0%{transform:scaleX(0) translateX(0)} 50%{transform:scaleX(0.6) translateX(60%)} 100%{transform:scaleX(0) translateX(250%)} }`}</style>
    </div>
  );
}
